import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import dayOfYear from 'dayjs/plugin/dayOfYear'
import * as yup from 'yup'

// Extend dayjs with the dayOfYear plugin
dayjs.extend(dayOfYear)

type DatePeriod = {
  startDate?: Dayjs | null | string
  endDate?: Dayjs | null | string
}

export default function validateNoOverlappingDatePeriods(periods?: DatePeriod[]) {
  if (!periods || periods.length <= 1) {
    return true
  }

  try {
    // Convert dates to day-of-year for comparison (ignoring year)
    const dateToDayOfYear = (date: Dayjs | null | undefined | string): number => {
      if (!date) throw new Error('No date provided')

      // Convert string to Dayjs if needed
      const dayjsDate = typeof date === 'string' ? dayjs(date) : date

      if (!dayjsDate.isValid()) throw new Error('Invalid date')
      return dayjsDate.dayOfYear()
    }

    // Filter valid periods and convert to day-of-year ranges
    const validPeriods = periods
      .filter((period) => {
        try {
          return dateToDayOfYear(period.startDate) && dateToDayOfYear(period.endDate)
        } catch {
          return false
        }
      })
      .flatMap((period) => {
        const start = dateToDayOfYear(period.startDate!)
        const end = dateToDayOfYear(period.endDate!)

        if (start <= end) {
          // Normal period - no wrapping
          return [{ start, end }]
        } else {
          // Wrap-around period - split into two periods
          // First part: from start date to end of year (day 365/366)
          // Second part: from beginning of year (day 1) to end date
          return [
            { start, end: 366 }, // Use 366 to handle leap years
            { start: 1, end }
          ]
        }
      })

    if (validPeriods.length <= 1) return true

    // Check for overlaps between all pairs of periods
    for (let i = 0; i < validPeriods.length; i++) {
      for (let j = i + 1; j < validPeriods.length; j++) {
        const period1 = validPeriods[i]!
        const period2 = validPeriods[j]!

        // Simple overlap check (allowing touching endpoints)
        if (period1.end >= period2.start && period1.start <= period2.end) {
          return false
        }
      }
    }

    return true
  } catch (error) {
    return true
  }
}

declare module 'yup' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ArraySchema<TIn, TContext, TDefault, TFlags> {
    /**
     * Validates that date periods do not overlap.
     * Touching endpoints are allowed (end date of one period can equal start date of next).
     * Handles wrap-around periods that span year boundaries.
     */
    noOverlappingDatePeriods(): this
  }
}

yup.addMethod(yup.array, 'noOverlappingDatePeriods', function () {
  return this.test('validateNoOverlappingDatePeriods', validateNoOverlappingDatePeriods)
})

export {}
